import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiSend, FiPrinter, FiCheck, FiX, FiArrowRight, FiTrash2, FiShare2, FiMail, FiPlus, FiEdit2, FiImage, FiEye, FiEyeOff } from 'react-icons/fi';
import SketchEditor from '../components/SketchEditor';
import RelatedLetters from '../components/RelatedLetters';
import axios from 'axios';
import { API_URL, API_BASE_URL } from '../config';
import { getAuthToken } from '../services/api';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

function QuoteView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set()); // partial invoicing
  // Approved change orders on this quote's jobs that nothing has billed yet. Converting a
  // quote used to ignore them entirely, so a CO could sit approved for months and never
  // reach an invoice. Opt-in per invoice — on a partial bill it's a genuine question
  // whether the CO rides along now or waits for the final one.
  const [billableCos, setBillableCos] = useState([]);
  const [includeCos, setIncludeCos] = useState(true);
  const [loading, setLoading] = useState(true);
  const [companySettings, setCompanySettings] = useState({});
  const [sketches, setSketches] = useState([]);
  const [editingSketch, setEditingSketch] = useState(null);
  // Email composer modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: '', subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    loadQuote();
    loadCompanySettings();
    loadSketches();
    loadBillableCos();
  }, [id]);

  const loadBillableCos = async () => {
    try {
      const config = await getAuthConfig();
      const res = await axios.get(`${API_URL}/quotes/${id}/billable-change-orders`, config);
      setBillableCos(res.data?.change_orders || []);
    } catch (error) {
      // Non-fatal: without this the checkbox just doesn't appear and billing works as before.
      console.error('Error loading billable change orders:', error);
      setBillableCos([]);
    }
  };

  const loadQuote = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/quotes/${id}`, config);
      setQuote(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading quote:', error);
      setLoading(false);
    }
  };

  const loadCompanySettings = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/quote-settings`, config);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Error loading company settings:', error);
    }
  };

  const handleSend = async () => {
    if (!window.confirm('Mark this quote as sent?')) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/quotes/${id}/send`, {}, config);
      loadQuote();
    } catch (error) {
      console.error('Error sending quote:', error);
      alert('Failed to send quote');
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Mark this quote as approved?')) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/quotes/${id}/approve`, {}, config);
      loadQuote();
    } catch (error) {
      console.error('Error approving quote:', error);
      alert('Failed to approve quote');
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('Mark this quote as declined?')) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/quotes/${id}/decline`, {}, config);
      loadQuote();
    } catch (error) {
      console.error('Error declining quote:', error);
      alert('Failed to decline quote');
    }
  };

  const coTotal = billableCos.reduce((sum, co) => sum + (co.amount || 0), 0);
  const chosenCoIds = () => (includeCos ? billableCos.map(co => co.id) : []);

  // Convert lives in the page header, far from the checkbox down by the line items, so both
  // actions restate the change-order decision in their confirm box. Quietly billing — or
  // quietly skipping — $700 is not a surprise worth saving a line of text for.
  const coConfirmSuffix = () => {
    if (!billableCos.length) return '';
    const lines = billableCos
      .map(co => `  - ${co.co_number ? co.co_number + ': ' : ''}${co.title} — ${formatCurrency(co.amount)}`)
      .join('\n');
    return includeCos
      ? `\n\nIncluding ${billableCos.length} approved change order(s), ${formatCurrency(coTotal)}:\n${lines}`
      : `\n\nNOT including ${billableCos.length} approved change order(s) worth ${formatCurrency(coTotal)} — they stay unbilled.`;
  };

  const coResultSuffix = (response) => {
    const n = response?.data?.change_orders_added || 0;
    if (!n) return '';
    return `\n\n${n} change order(s) added, ${formatCurrency(response.data.change_orders_total || 0)}.`;
  };

  const handleConvert = async () => {
    const remaining = (quote?.line_items || []).filter(li => !li.is_invoiced).length;
    const msg = remaining < (quote?.line_items || []).length
      ? `Invoice the ${remaining} remaining item(s) on this quote?`
      : 'Convert this quote to an invoice?';
    if (!window.confirm(msg + coConfirmSuffix())) return;
    try {
      const config = await getAuthConfig();
      const response = await axios.post(
        `${API_URL}/quotes/${id}/convert`, { change_order_ids: chosenCoIds() }, config);
      alert(`Invoice ${response.data.invoice.invoice_number} created.${coResultSuffix(response)}`);
      navigate(`/billing/invoices/${response.data.invoice.id}`);
    } catch (error) {
      console.error('Error converting quote:', error);
      alert(error.response?.data?.error || 'Failed to convert quote');
    }
  };

  // Partial invoicing: bill only the selected (not-yet-invoiced) line items.
  const handleInvoiceSelected = async () => {
    const ids = Array.from(selectedItems);
    if (!ids.length) return;
    if (!window.confirm(`Create an invoice for the ${ids.length} selected item(s)?${coConfirmSuffix()}`)) return;
    try {
      const config = await getAuthConfig();
      const response = await axios.post(
        `${API_URL}/quotes/${id}/invoice-items`,
        { line_item_ids: ids, change_order_ids: chosenCoIds() },
        config);
      const left = response.data.remaining_items;
      alert(`${response.data.message}${left ? `\n\n${left} item(s) still left to invoice on this quote.` : '\n\nAll items on this quote are now invoiced.'}`);
      navigate(`/billing/invoices/${response.data.invoice.id}`);
    } catch (error) {
      console.error('Error invoicing items:', error);
      alert(error.response?.data?.error || 'Failed to create invoice');
    }
  };

  const handlePrint = async () => {
    const token = await getAuthToken();
    const url = token 
      ? `${API_URL}/quotes/${id}/pdf?token=${token}`
      : `${API_URL}/quotes/${id}/pdf`;
    window.open(url, '_blank');
  };

  const handleShare = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.post(`${API_URL}/quotes/${id}/share-token`, {}, config);
      const shareToken = response.data.share_token;
      const publicUrl = `${window.location.origin}/quote/${shareToken}`;

      if (navigator.share) {
        await navigator.share({
          title: `Quote ${quote.quote_number}`,
          text: `Quote ${quote.quote_number} from North Georgia Surveying - Total: $${(quote.total || 0).toFixed(2)}`,
          url: publicUrl
        });
      } else {
        await navigator.clipboard.writeText(publicUrl);
        alert('Quote link copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
        alert('Failed to generate share link');
      }
    }
  };

  const handleEmail = () => {
    const email = quote.bill_to_email;
    if (!email) {
      alert('No email address on file for this quote. Add one in the billing details.');
      return;
    }
    const companyName = companySettings.company_name || 'North Georgia Surveying';
    const firstName = (quote.bill_to_name || '').split(' ')[0] || '';
    setEmailForm({
      to: email,
      subject: `Quote ${quote.quote_number} from ${companyName}`,
      message: firstName ? `Hi ${firstName},\n\nPlease find your quote attached below. Let me know if you have any questions.\n\nThanks,\nPaul` : '',
    });
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.to.trim()) { alert('Recipient email is required'); return; }
    setSendingEmail(true);
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/quotes/${id}/email`, {
        to_email: emailForm.to.trim(),
        subject: emailForm.subject.trim(),
        message: emailForm.message,
      }, config);
      setShowEmailModal(false);
      alert(`Quote sent to ${emailForm.to.trim()}`);
      loadQuote();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send email');
    }
    setSendingEmail(false);
  };

  const loadSketches = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/quotes/${id}/sketches`, config);
      setSketches(response.data || []);
    } catch (error) {
      console.error('Error loading sketches:', error);
    }
  };

  const handleCreateSketch = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.post(`${API_URL}/quotes/${id}/sketches`, {}, config);
      setSketches(prev => [...prev, response.data]);
      setEditingSketch(response.data);
    } catch (error) {
      alert('Failed to create sketch');
    }
  };

  const handleDeleteSketch = async (sketchId) => {
    if (!window.confirm('Delete this sketch?')) return;
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/quotes/${id}/sketches/${sketchId}`, config);
      setSketches(prev => prev.filter(s => s.id !== sketchId));
    } catch (error) {
      alert('Failed to delete sketch');
    }
  };

  const handleTogglePdf = async (sketchId, showOnPdf) => {
    try {
      const config = await getAuthConfig();
      await axios.put(`${API_URL}/quotes/${id}/sketches/${sketchId}`, { show_on_pdf: !showOnPdf }, config);
      setSketches(prev => prev.map(s => s.id === sketchId ? { ...s, show_on_pdf: !showOnPdf } : s));
    } catch (error) {
      alert('Failed to update sketch');
    }
  };

  const handleRenameSketch = async (sketchId, currentName) => {
    const newName = prompt('Rename sketch:', currentName);
    if (!newName || newName === currentName) return;
    try {
      const config = await getAuthConfig();
      await axios.put(`${API_URL}/quotes/${id}/sketches/${sketchId}`, { name: newName }, config);
      setSketches(prev => prev.map(s => s.id === sketchId ? { ...s, name: newName } : s));
    } catch (error) {
      alert('Failed to rename sketch');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this quote? This cannot be undone.')) return;
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/quotes/${id}`, config);
      navigate('/billing?tab=quotes');
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert('Failed to delete quote');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Timestamps are stored naive UTC — append 'Z' so they display in local time.
  const formatStamp = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr.endsWith('Z') ? isoStr : isoStr + 'Z');
    if (isNaN(d)) return '';
    return d.toLocaleString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
    });
  };

  const getStatusStyle = (status) => {
    const styles = {
      'Draft': { bg: '#e5e7eb', color: '#374151' },
      'Sent': { bg: '#dbeafe', color: '#1d4ed8' },
      'Approved': { bg: '#dcfce7', color: '#166534' },
      'Declined': { bg: '#fee2e2', color: '#dc2626' },
      'Expired': { bg: '#fef3c7', color: '#b45309' },
      'Converted': { bg: '#ede9fe', color: '#7c3aed' }
    };
    return styles[status] || styles['Draft'];
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading quote...</div>;
  }

  if (!quote) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Quote not found</div>;
  }

  const statusStyle = getStatusStyle(quote.status);
  const isMobile = window.innerWidth < 768;
  // Partial invoicing is offered once a quote is approved (and while any line is unbilled).
  // The backend still enforces can_edit_quotes.
  // 'Partially Approved' is invoiceable too — the client approved some of the work and
  // we still have to bill it. Declined lines are excluded below (and server-side).
  const canInvoice = quote.status === 'Approved' || quote.status === 'Partially Approved' || quote.status === 'Partially Invoiced';
  const isDeclinedItem = (item) => item.approval_status === 'declined';
  const hasBillableUnbilled = (quote.line_items || []).some(li => !li.is_invoiced && !isDeclinedItem(li));

  const actionBtnStyle = (bg, color, border) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: isMobile ? '8px 14px' : '10px 20px',
    backgroundColor: bg,
    color: color,
    border: border || 'none',
    borderRadius: '8px',
    fontSize: isMobile ? '13px' : '14px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  });

  return (
    <div style={{ padding: isMobile ? '12px' : '20px', maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/billing?tab=quotes'); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            marginBottom: '12px',
            padding: '0'
          }}
        >
          <FiArrowLeft /> Back to Quotes
        </button>
        <h1 style={{ margin: '0 0 16px', fontSize: isMobile ? '22px' : '28px', color: '#1f2937' }}>
          Quote {quote.quote_number}
        </h1>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {quote.status !== 'Converted' && (
            <button onClick={() => navigate(`/billing/quotes/${id}/edit`)} style={actionBtnStyle('#f3f4f6', '#374151')}>
              <FiEdit size={16} /> Edit
            </button>
          )}
          {quote.status === 'Sent' && (
            <>
              <button onClick={handleApprove} style={actionBtnStyle('#16a34a', 'white')}>
                <FiCheck size={16} /> Approve
              </button>
              <button onClick={handleDecline} style={actionBtnStyle('#dc2626', 'white')}>
                <FiX size={16} /> Decline
              </button>
            </>
          )}
          {quote.status === 'Draft' && (
            <button onClick={handleSend} style={actionBtnStyle('#2563eb', 'white')}>
              <FiSend size={16} /> Send
            </button>
          )}
          {(quote.status === 'Approved' || quote.status === 'Partially Approved') && !quote.converted_to_invoice_id && (
            <button onClick={handleConvert} style={actionBtnStyle('#7c3aed', 'white')}>
              <FiArrowRight size={16} /> Convert
            </button>
          )}
          <button onClick={handlePrint} style={actionBtnStyle('#f3f4f6', '#374151')}>
            <FiPrinter size={16} /> Print
          </button>
          <button onClick={handleShare} style={actionBtnStyle('#f0fdf4', '#16a34a', '1px solid #86efac')}>
            <FiShare2 size={16} /> Share
          </button>
          <button onClick={handleEmail} style={actionBtnStyle('#eff6ff', '#2563eb', '1px solid #93c5fd')}>
            <FiMail size={16} /> Email
          </button>
          <button onClick={handleDelete} style={actionBtnStyle('#fef2f2', '#dc2626')}>
            <FiTrash2 size={16} /> Delete
          </button>
        </div>
      </div>

      {/* Converted Notice */}
      {quote.converted_to_invoice_id && (
        <div style={{
          backgroundColor: '#ede9fe',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span style={{ color: '#7c3aed', fontWeight: '500' }}>
            This quote has been converted to an invoice
          </span>
          <button
            onClick={() => navigate(`/billing/invoices/${quote.converted_to_invoice_id}`)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            View Invoice
          </button>
        </div>
      )}

      {/* Quote Preview */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: isMobile ? '16px' : '40px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflowX: 'hidden'
      }}>
        {/* Quote Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: isMobile ? '24px' : '40px',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '0'
        }}>
          <div>
            {companySettings.company_logo && (
              <img
                src={`${API_BASE_URL}${companySettings.company_logo}`}
                alt="Company Logo"
                style={{ maxHeight: '60px', maxWidth: '160px', marginBottom: '10px', display: 'block' }}
              />
            )}
            <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '6px' }}>
              {companySettings.company_name || 'Your Company Name'}
            </div>
            <div style={{ color: '#666', fontSize: '13px', lineHeight: '1.6' }}>
              {companySettings.company_address && <div>{companySettings.company_address}</div>}
              {companySettings.company_city_state_zip && <div>{companySettings.company_city_state_zip}</div>}
              {companySettings.company_phone && <div>{companySettings.company_phone}</div>}
              {companySettings.company_email && <div>{companySettings.company_email}</div>}
            </div>
          </div>
          <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: isMobile ? 'flex-start' : 'flex-end', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: isMobile ? '28px' : '36px', color: '#16a34a', margin: '0' }}>QUOTE</h1>
              <span style={{ fontSize: '16px', color: '#666' }}>{quote.quote_number}</span>
              <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '13px',
                fontWeight: '600',
                backgroundColor: statusStyle.bg,
                color: statusStyle.color
              }}>
                {quote.status}
              </span>
            </div>
          </div>
        </div>

        {/* Bill To & Meta */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: isMobile ? '24px' : '40px',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '0'
        }}>
          <div>
            <h3 style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase', margin: '0 0 8px' }}>
              Quote For
            </h3>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
              {quote.bill_to_name}
            </div>
            <div style={{ color: '#555', fontSize: '14px', lineHeight: '1.6' }}>
              {quote.bill_to_company && <div>{quote.bill_to_company}</div>}
              {quote.bill_to_address && <div>{quote.bill_to_address}</div>}
              {quote.bill_to_city_state_zip && <div>{quote.bill_to_city_state_zip}</div>}
              {quote.bill_to_email && <div>{quote.bill_to_email}</div>}
              {quote.bill_to_phone && <div>{quote.bill_to_phone}</div>}
            </div>
          </div>
          <div style={{ textAlign: isMobile ? 'left' : 'right', display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: isMobile ? '16px' : '0', flexWrap: 'wrap' }}>
            <div style={{ marginBottom: isMobile ? '0' : '12px' }}>
              <div style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Quote Date</div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>{formatDate(quote.quote_date)}</div>
            </div>
            <div style={{ marginBottom: isMobile ? '0' : '12px' }}>
              <div style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Valid Until</div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>{formatDate(quote.valid_until)}</div>
            </div>
            {quote.po_number && (
              <div>
                <div style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>PO Number</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>{quote.po_number}</div>
              </div>
            )}
          </div>
        </div>

        {/* Approval audit stamp — the record we stand on if the price is ever disputed.
            Only claims an e-signature when one was actually captured; quotes approved
            before signature capture existed show a plain dated record. */}
        {quote.approved_at && (() => {
          const signedByClient = !!quote.approved_by_name && quote.approval_source === 'client';
          return (
            <div style={{
              marginBottom: '24px',
              padding: '14px 18px',
              borderRadius: '8px',
              border: `1px solid ${signedByClient ? '#86efac' : '#e5e7eb'}`,
              borderLeft: `4px solid ${signedByClient ? '#16a34a' : '#6b7280'}`,
              backgroundColor: signedByClient ? '#f0fdf4' : '#f9fafb'
            }}>
              <div style={{
                fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px',
                color: signedByClient ? '#15803d' : '#6b7280', marginBottom: '6px'
              }}>
                {signedByClient ? 'Client Approval' : 'Approval Record'}
              </div>
              {signedByClient ? (
                <>
                  <div style={{ fontSize: '14px', color: '#166534' }}>
                    Signed by <strong>{quote.approved_by_name}</strong> on {formatStamp(quote.approved_at)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '6px', lineHeight: '1.5', wordBreak: 'break-word' }}>
                    Electronic signature (ESIGN / Georgia UETA)
                    {quote.approval_ip ? ` · IP ${quote.approval_ip}` : ''}
                    {quote.approval_user_agent ? <><br />Device: {quote.approval_user_agent}</> : null}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: '14px', color: '#374151' }}>
                  {quote.approval_source === 'office' && quote.approved_by_name
                    ? <>Recorded in-office by <strong>{quote.approved_by_name}</strong> on {formatStamp(quote.approved_at)}</>
                    : <>Approved on {formatStamp(quote.approved_at)}</>}
                </div>
              )}
            </div>
          );
        })()}

        {/* Related Jobs */}
        {quote.jobs && quote.jobs.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase', margin: '0 0 8px' }}>
              Related Jobs
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {quote.jobs.map(job => (
                <span
                  key={job.id}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#ecfdf5',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#16a34a'
                  }}
                >
                  {job.job_number} - {job.property_address}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Property / Parcel Information */}
        {(quote.parcel_id || quote.parcel_owner || quote.parcel_address || quote.parcel_acreage) && (
          <div style={{
            marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb', borderRadius: '8px'
          }}>
            <h3 style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase', margin: '0 0 12px' }}>
              Property Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px 20px', fontSize: '14px' }}>
              {quote.parcel_id && <div><strong>Parcel #:</strong> {quote.parcel_id}</div>}
              {quote.parcel_county && <div><strong>County:</strong> {quote.parcel_county}</div>}
              {quote.parcel_owner && <div><strong>Owner:</strong> {quote.parcel_owner}</div>}
              {quote.parcel_address && <div><strong>Address:</strong> {quote.parcel_address}</div>}
              {quote.parcel_acreage && <div><strong>Acreage:</strong> {quote.parcel_acreage}</div>}
              {quote.parcel_land_lot && <div><strong>Land Lot:</strong> {quote.parcel_land_lot}</div>}
              {quote.parcel_district && <div><strong>District:</strong> {quote.parcel_district}</div>}
              {quote.parcel_zoning && <div><strong>Zoning:</strong> {quote.parcel_zoning}</div>}
            </div>
            {quote.parcel_legal_description && (
              <div style={{ marginTop: '8px', fontSize: '14px' }}><strong>Legal Description:</strong> {quote.parcel_legal_description}</div>
            )}
            {quote.parcel_deeds && (
              <div style={{ marginTop: '8px', fontSize: '14px' }}><strong>Deeds:</strong> {quote.parcel_deeds}</div>
            )}
            {quote.parcel_plats && (
              <div style={{ marginTop: '8px', fontSize: '14px' }}><strong>Plats:</strong> {quote.parcel_plats}</div>
            )}
          </div>
        )}

        {/* Line Items */}
        {isMobile ? (
          <div style={{ marginBottom: '24px' }}>
            {quote.line_items?.map((item, index) => (
              <div key={index} style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ fontWeight: '500', marginBottom: '4px' }}>{item.description}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280' }}>
                  <span>{item.quantity} x {formatCurrency(item.rate)}</span>
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{formatCurrency(item.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid #e5e7eb' }}>Description</th>
                <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid #e5e7eb', width: '80px' }}>Qty</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid #e5e7eb', width: '120px' }}>Rate</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid #e5e7eb', width: '120px' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {quote.line_items?.map((item, index) => (
                <tr key={item.id || index} style={{ borderBottom: '1px solid #e5e7eb', background: item.is_invoiced ? '#f9fafb' : 'transparent' }}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      {canInvoice && (
                        (item.is_invoiced || isDeclinedItem(item)) ? (
                          <span style={{ width: 16, flexShrink: 0 }} />
                        ) : (
                          <input
                            type="checkbox"
                            checked={selectedItems.has(item.id)}
                            onChange={(e) => {
                              const next = new Set(selectedItems);
                              if (e.target.checked) next.add(item.id); else next.delete(item.id);
                              setSelectedItems(next);
                            }}
                            style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }}
                          />
                        )
                      )}
                      <div>
                        <div style={{
                          color: (item.is_invoiced || isDeclinedItem(item)) ? '#6b7280' : 'inherit',
                          textDecoration: isDeclinedItem(item) ? 'line-through' : 'none'
                        }}>
                          {item.description}
                        </div>
                        {isDeclinedItem(item) && (
                          <span style={{ display: 'inline-block', marginTop: 4, background: '#fee2e2', color: '#b91c1c', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                            Declined by client — not billable
                          </span>
                        )}
                        {item.is_invoiced && (
                          <button
                            onClick={() => navigate(`/billing/invoices/${item.invoiced_invoice_id}`)}
                            style={{ marginTop: 4, background: '#ede9fe', color: '#7c3aed', border: 'none', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                          >
                            ✓ Billed on {item.invoiced_invoice_number}
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', color: (item.is_invoiced || isDeclinedItem(item)) ? '#6b7280' : 'inherit' }}>{item.quantity}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', color: (item.is_invoiced || isDeclinedItem(item)) ? '#6b7280' : 'inherit' }}>{formatCurrency(item.rate)}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: (item.is_invoiced || isDeclinedItem(item)) ? '#6b7280' : 'inherit', textDecoration: isDeclinedItem(item) ? 'line-through' : 'none' }}>{formatCurrency(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Approved change orders with nothing billing them yet. Shown independently of the
            partial-invoicing bar: a CO can still be outstanding after every quote line is billed. */}
        {canInvoice && billableCos.length > 0 && (
          <div style={{
            background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
            padding: '12px 14px', marginBottom: 16,
          }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeCos}
                onChange={(e) => setIncludeCos(e.target.checked)}
                style={{ width: 16, height: 16, marginTop: 3, flexShrink: 0 }}
              />
              <div style={{ fontSize: 13, color: '#78350f', flex: 1 }}>
                <strong>
                  Also bill {billableCos.length} approved change order{billableCos.length > 1 ? 's' : ''} — {formatCurrency(coTotal)}
                </strong>
                <div style={{ marginTop: 6 }}>
                  {billableCos.map(co => (
                    <div key={co.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '2px 0' }}>
                      <span>
                        {co.co_number ? `${co.co_number}: ` : ''}{co.title}
                        {co.date_approved ? ` — approved ${co.date_approved}` : ''}
                      </span>
                      <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatCurrency(co.amount)}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 6, color: '#92400e' }}>
                  {includeCos
                    ? 'Added to the invoice as their own line items, linked to the change order.'
                    : 'Unticked — these stay unbilled and keep showing as outstanding on the job.'}
                </div>
              </div>
            </label>
          </div>
        )}

        {/* Partial invoicing bar */}
        {canInvoice && hasBillableUnbilled && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
            background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '12px 14px', marginBottom: 16,
          }}>
            <div style={{ fontSize: 13, color: '#4c1d95' }}>
              {selectedItems.size > 0
                ? <>Invoice <strong>{selectedItems.size}</strong> selected item(s) now — the rest stay on the quote for later.</>
                : <>Tick the items you're ready to bill, or use <strong>Convert</strong> to invoice everything remaining.</>}
            </div>
            <button
              onClick={handleInvoiceSelected}
              disabled={selectedItems.size === 0}
              style={{
                padding: '8px 14px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 13,
                background: selectedItems.size ? '#7c3aed' : '#e5e7eb',
                color: selectedItems.size ? 'white' : '#9ca3af',
                cursor: selectedItems.size ? 'pointer' : 'default',
              }}
            >
              Invoice Selected
            </button>
          </div>
        )}

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: isMobile ? '100%' : '300px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
              <span style={{ color: '#666' }}>Subtotal</span>
              <span style={{ fontWeight: '500' }}>{formatCurrency(quote.subtotal)}</span>
            </div>
            {quote.discount_percent > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ color: '#666' }}>Discount ({quote.discount_percent}%)</span>
                <span style={{ fontWeight: '500', color: '#dc2626' }}>-{formatCurrency(quote.discount_amount)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', backgroundColor: '#f9fafb', marginTop: '8px', borderRadius: '8px', paddingLeft: '16px', paddingRight: '16px' }}>
              <span style={{ fontSize: '18px', fontWeight: '700' }}>Total</span>
              <span style={{ fontSize: '18px', fontWeight: '700', color: '#16a34a' }}>{formatCurrency(quote.total)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {quote.notes && (
          <div style={{ marginTop: '40px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '14px', color: '#666', textTransform: 'uppercase' }}>Notes</h3>
            <p style={{ margin: 0, color: '#374151', whiteSpace: 'pre-wrap' }}>{quote.notes}</p>
          </div>
        )}

        {/* Terms */}
        {quote.terms && (
          <div style={{ marginTop: '24px' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '14px', color: '#666', textTransform: 'uppercase' }}>Terms</h3>
            <p style={{ margin: 0, color: '#374151' }}>{quote.terms}</p>
          </div>
        )}
      </div>

      {/* Sketches Section */}
      <div style={{
        marginTop: '24px', padding: '20px', backgroundColor: 'white',
        borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiImage /> Sketches ({sketches.length})
          </h3>
          <button
            onClick={handleCreateSketch}
            style={{
              padding: '8px 16px', backgroundColor: '#2563eb', color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
              fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            <FiPlus size={14} /> New Sketch
          </button>
        </div>

        {sketches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: '#9ca3af' }}>
            <FiImage size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>No sketches yet. Create one to show the client what you're proposing.</p>
            <p style={{ margin: '8px 0 0', fontSize: '13px' }}>Upload a screenshot of the parcel map from qPublic as a background, then draw on top of it.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
            {sketches.map(sketch => (
              <div key={sketch.id} style={{
                border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#f9fafb'
              }}>
                <div
                  onClick={() => setEditingSketch(sketch)}
                  style={{
                    height: '180px', backgroundColor: '#e5e7eb', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundImage: sketch.thumbnail ? `url(${API_URL.replace('/api', '')}${sketch.thumbnail})` : 'none',
                    backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat'
                  }}
                >
                  {!sketch.thumbnail && (
                    <div style={{ color: '#9ca3af', textAlign: 'center' }}>
                      <FiEdit2 size={24} />
                      <div style={{ fontSize: '12px', marginTop: '4px' }}>Click to edit</div>
                    </div>
                  )}
                </div>
                <div style={{ padding: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <strong
                      onClick={() => handleRenameSketch(sketch.id, sketch.name)}
                      style={{ cursor: 'pointer', fontSize: '14px' }}
                      title="Click to rename"
                    >
                      {sketch.name}
                    </strong>
                    <button
                      onClick={() => handleTogglePdf(sketch.id, sketch.show_on_pdf)}
                      title={sketch.show_on_pdf ? 'Showing on PDF' : 'Hidden from PDF'}
                      style={{
                        padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px',
                        backgroundColor: sketch.show_on_pdf ? '#dcfce7' : '#f5f5f5',
                        color: sketch.show_on_pdf ? '#16a34a' : '#999',
                        cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px'
                      }}
                    >
                      {sketch.show_on_pdf ? <><FiEye size={12} /> PDF</> : <><FiEyeOff size={12} /> Hidden</>}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => setEditingSketch(sketch)}
                      style={{
                        flex: 1, padding: '6px', border: '1px solid #bfdbfe', borderRadius: '4px',
                        backgroundColor: '#eff6ff', color: '#2563eb', cursor: 'pointer',
                        fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                      }}
                    >
                      <FiEdit2 size={12} /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteSketch(sketch.id)}
                      style={{
                        padding: '6px 10px', border: '1px solid #fecaca', borderRadius: '4px',
                        backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '12px'
                      }}
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Email Composer Modal */}
      {showEmailModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            width: '100%', maxWidth: '600px', maxHeight: 'calc(90dvh - 90px)', overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: '14px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiMail color="#2563eb" /> Email Quote
              </h2>
              <button onClick={() => setShowEmailModal(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px',
              }}>
                <FiX size={20} />
              </button>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>To</label>
              <input
                type="email"
                value={emailForm.to}
                onChange={(e) => setEmailForm(p => ({ ...p, to: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Subject</label>
              <input
                type="text"
                value={emailForm.subject}
                onChange={(e) => setEmailForm(p => ({ ...p, subject: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                Message <span style={{ color: '#9ca3af', fontWeight: '400' }}>(appears above the quote)</span>
              </label>
              <textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm(p => ({ ...p, message: e.target.value }))}
                rows={8}
                placeholder="Add a personal note... (optional)"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            <div style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#f9fafb', padding: '10px 12px', borderRadius: '6px' }}>
              The full quote (line items, totals, sketches) plus an Approve/Decline button will be included automatically below your message.
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
              <button
                onClick={() => setShowEmailModal(false)}
                disabled={sendingEmail}
                style={{ padding: '10px 18px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailForm.to.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: sendingEmail || !emailForm.to.trim() ? '#9ca3af' : '#2563eb',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: sendingEmail || !emailForm.to.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                <FiSend size={14} /> {sendingEmail ? 'Sending...' : 'Send Quote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sketch Editor Modal */}
      {editingSketch && (
        <SketchEditor
          quoteId={id}
          sketchId={editingSketch.id}
          canvasJson={editingSketch.canvas_json}
          backgroundUrl={editingSketch.background_image}
          onSave={() => { setEditingSketch(null); loadSketches(); }}
          onClose={() => setEditingSketch(null)}
        />
      )}

      {/* Internal Notes (not shown on printed quote) */}
      {quote.internal_notes && (
        <div style={{
          backgroundColor: '#fef3c7',
          borderRadius: '12px',
          padding: '20px',
          marginTop: '24px'
        }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '14px', color: '#b45309' }}>Internal Notes</h3>
          <p style={{ margin: 0, color: '#92400e', whiteSpace: 'pre-wrap' }}>{quote.internal_notes}</p>
        </div>
      )}

      {/* Letters referencing this quote */}
      <RelatedLetters relatedType="quote" relatedId={parseInt(id, 10)} />
    </div>
  );
}

export default QuoteView;